// ============================================================================
// MathText — render a plain-text string that may contain KaTeX-style math
// ($inline$ / $$display$$, also \[ \] and \( \)) plus **bold** spans, as React
// elements. Used by the Exam paper preview where questions are structured data
// (not Markdown documents), so renderRichContent/MarkdownMath don't fit.
//
// The model output is imperfect: delimiters occasionally wrap onto a newline,
// backslashes get doubled, or a literal "$" (dollar amount) sits next to math.
// The tokenizer below is deliberately tolerant of all of those, and KaTeX gets
// a normalized second chance if the first render errors.
// ============================================================================

import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const MathError: React.FC<{ math: string }> = ({ math }) => (
  <span style={{ color: '#dc2626', fontFamily: 'monospace', fontSize: '0.92em' }}>{math}</span>
);

/** Looks like actual math rather than a dollar amount / prose? */
function looksLikeMath(inner: string): boolean {
  // Contains a LaTeX command, sub/superscript, or grouped braces.
  return /\\[a-zA-Z]+|[_{}]|[\\^]/.test(inner);
}

/**
 * Clean up model quirks inside a math span: doubled backslashes, embedded
 * newlines/extra spaces, and a stray $ or delimiter left inside.
 */
function normalizeMath(raw: string): string {
  let s = raw.trim();
  // Strip an accidental inner pair of delimiters ("$$x$$" captured with them).
  s = s.replace(/^\$+/, '').replace(/\$+$/, '');
  s = s.replace(/^\\\(|^\\\[/, '').replace(/\\\)$|\\\]$/, '');
  // "\\frac" coming through literally -> "\frac".
  s = s.replace(/\\\\(?=[a-zA-Z])/g, '\\');
  // HTML-escaped entities inside math ("&lt;" for "<") — KaTeX needs the real
  // character. Decode &amp; last so double-escaped sequences unwrap once.
  s = s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&amp;/g, '&');
  // Newlines / runs of whitespace (KaTeX treats newlines as text characters).
  s = s.replace(/\s*\n\s*/g, ' ').replace(/\s{2,}/g, ' ');
  return s.trim();
}

type Token = { kind: 'display' | 'inline' | 'text'; value: string };

/**
 * Pair up math delimiters in order. $$…$$ / \[…\] are always math; $…$ is math
 * only when the content looks mathematical (avoids treating "$5 and $10" as a
 * formula). Delimiters may span newlines.
 */
function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let buf = '';

  const flush = () => {
    if (buf) {
      tokens.push({ kind: 'text', value: buf });
      buf = '';
    }
  };

  while (i < text.length) {
    const rest = text.slice(i);

    // Display: $$ ... $$
    let m = /^\$\$([\s\S]+?)\$\$/.exec(rest);
    if (m) {
      flush();
      tokens.push({ kind: 'display', value: normalizeMath(m[1]) });
      i += m[0].length;
      continue;
    }

    // Display: \[ ... \]
    m = /^\\\[([\s\S]+?)\\\]/.exec(rest);
    if (m) {
      flush();
      tokens.push({ kind: 'display', value: normalizeMath(m[1]) });
      i += m[0].length;
      continue;
    }

    // Inline: \( ... \)
    m = /^\\\(([\s\S]+?)\\\)/.exec(rest);
    if (m) {
      flush();
      tokens.push({ kind: 'inline', value: normalizeMath(m[1]) });
      i += m[0].length;
      continue;
    }

    // Inline: $ ... $ (may contain a newline). Only accept it as math when the
    // content looks mathematical; otherwise treat the $ as a literal character.
    m = /^\$([^$]+?)\$/.exec(rest);
    if (m && looksLikeMath(m[1])) {
      flush();
      tokens.push({ kind: 'inline', value: normalizeMath(m[1]) });
      i += m[0].length;
      continue;
    }

    buf += text[i];
    i += 1;
  }
  flush();
  return tokens;
}

/** Math node that retries once with a normalized expression on KaTeX error. */
const MathNode: React.FC<{ math: string; block: boolean }> = ({ math, block }) => {
  const [attempt, setAttempt] = React.useState(0);
  const expr = attempt === 0 ? math : normalizeMath(math);
  const Comp = block ? BlockMath : InlineMath;

  return (
    <Comp
      math={expr}
      renderError={() => {
        if (attempt === 0 && expr !== normalizeMath(expr)) {
          // Retry on the next tick with the fully normalized expression.
          setTimeout(() => setAttempt(1), 0);
          return <span style={{ opacity: 0.55 }}>{expr}</span>;
        }
        return <MathError math={expr} />;
      }}
    />
  );
};

function renderPlain(text: string, keyPrefix: string): React.ReactNode[] {
  // Minimal **bold** support; newlines become <br/>.
  const nodes: React.ReactNode[] = [];
  text.split(/(\*\*[^*]+\*\*)/g).forEach((part, i) => {
    if (!part) return;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>);
    } else {
      part.split('\n').forEach((line, j) => {
        if (j > 0) nodes.push(<br key={`${keyPrefix}-br${i}-${j}`} />);
        if (line) nodes.push(line);
      });
    }
  });
  return nodes;
}

const MathText: React.FC<{ text: string; block?: boolean }> = ({ text, block }) => {
  const tokens = tokenize(text || '');

  return (
    <>
      {tokens.map((tok, index) => {
        if (tok.kind === 'text') {
          return <React.Fragment key={index}>{renderPlain(tok.value, `p${index}`)}</React.Fragment>;
        }
        const isBlock = tok.kind === 'display' || (block === true && tok.kind === 'inline');
        return <MathNode key={index} math={tok.value} block={isBlock} />;
      })}
    </>
  );
};

/** Strip KaTeX/markdown decorations for plain-text fallbacks. */
export function stripMath(text: string): string {
  return (text || '')
    .replace(/\$\$([^$]+)\$\$/g, '$1')
    .replace(/\$([^$\n]+)\$/g, '$1')
    .replace(/\\\[([^\]]+)\\\]/g, '$1')
    .replace(/\\\(([^)]+)\\\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1');
}

export default MathText;
